import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Eye, EyeOff, Trash2, Loader2, Sparkles, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

export const AdminReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
      setReviews(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  const moderate = async (id, payload) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}/moderate`, payload, { withCredentials: true });
      toast.success('Avis mis à jour');
      fetchReviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
      toast.success('Avis supprimé');
      fetchReviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const counts = {
    all: reviews.length,
    pending: reviews.filter(r => !r.public).length,
    published: reviews.filter(r => r.public && !r.featured).length,
    featured: reviews.filter(r => r.featured).length,
  };

  const filtered = reviews.filter(r => {
    if (filter === 'pending') return !r.public;
    if (filter === 'published') return r.public && !r.featured;
    if (filter === 'featured') return r.featured;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-reviews-page">
      <div className="flex items-center gap-3">
        <Link to="/app" className="text-zinc-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight font-['Outfit']">Modération des avis</h1>
          <p className="text-zinc-400 text-sm">Valide quels avis affichés publiquement, mets en avant les meilleurs sur la landing.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all',       label: 'Tous',         count: counts.all },
          { id: 'pending',   label: 'En attente',   count: counts.pending },
          { id: 'published', label: 'Publiés',      count: counts.published },
          { id: 'featured',  label: 'Mis en avant', count: counts.featured },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === t.id
                ? 'bg-amber-500 text-zinc-950'
                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800'
            }`}
            data-testid={`admin-reviews-filter-${t.id}`}
          >
            {t.label} <span className="opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500">
            Aucun avis dans cette catégorie.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <Card key={r.id} className="bg-zinc-900 border-zinc-800" data-testid={`admin-review-${r.id}`}>
              <CardContent className="p-5">
                <div className="space-y-4">
                  {/* Bandeau header */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-zinc-700'}`} />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-white">{r.user_name || '—'}</span>
                    <span className="text-xs text-zinc-500 font-mono break-all">{r.user_email}</span>
                    {r.featured && <span className="text-[10px] uppercase tracking-wider bg-amber-500 text-zinc-950 px-2 py-0.5 rounded font-bold">Mis en avant</span>}
                    {r.public && !r.featured && <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-semibold">Publié</span>}
                    {!r.public && <span className="text-[10px] uppercase tracking-wider bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">En attente</span>}
                  </div>

                  {/* Commentaire */}
                  {r.comment ? (
                    <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{r.comment}</p>
                  ) : (
                    <p className="text-zinc-600 text-sm italic">Aucun commentaire</p>
                  )}

                  {/* Date */}
                  <p className="text-[10px] text-zinc-600 font-mono">
                    Créé le {new Date(r.created_at).toLocaleString('fr-FR')}
                    {r.updated_at && ` · Modifié le ${new Date(r.updated_at).toLocaleString('fr-FR')}`}
                  </p>

                  {/* Actions — toujours visibles, layout flex-wrap pour ne jamais déborder */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-800">
                    {!r.public ? (
                      <Button
                        size="sm"
                        onClick={() => moderate(r.id, { public: true })}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        data-testid={`admin-review-publish-${r.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Publier
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moderate(r.id, { public: false, featured: false })}
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                        data-testid={`admin-review-hide-${r.id}`}
                      >
                        <EyeOff className="w-4 h-4 mr-1" /> Dépublier
                      </Button>
                    )}
                    {r.public && (
                      <Button
                        size="sm"
                        onClick={() => moderate(r.id, { featured: !r.featured })}
                        className={r.featured
                          ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                          : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30'}
                        data-testid={`admin-review-feature-${r.id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-1" /> {r.featured ? 'Retirer mise en avant' : 'Mettre en avant'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => remove(r.id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 ml-auto"
                      data-testid={`admin-review-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviewsPage;
